import { ResponsiveContainer, Tooltip, CartesianGrid, XAxis, YAxis, LineChart, Line } from 'recharts';
import { ChevronDown, Calendar } from 'lucide-react';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getParsedCsvData, getDataForDay, getAvailableDays } from '../../data/findhorn_data.js';
import HorseAnimation from '../HorseAnimation';

// Timeline Dropdown Component
const TimelineDropdown = ({ selectedPeriod, onPeriodChange, label = "Timeline" }) => {
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [selectedOption, setSelectedOption] = useState('today');
    const [customStartDate, setCustomStartDate] = useState('');
    const [customEndDate, setCustomEndDate] = useState('');
    const dropdownRef = useRef(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        function handleClickOutside(event) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Generate period options for the past 2 years plus CSV days
    const generatePeriodOptions = () => {
        const options = [];
        const today = new Date();

        // Single day options
        options.push({
            value: 'today',
            label: 'Today',
            startDate: today,
            endDate: today,
            type: 'day'
        });

        const yesterday = new Date(today);
        yesterday.setDate(today.getDate() - 1);
        options.push({
            value: 'yesterday',
            label: 'Yesterday',
            startDate: yesterday,
            endDate: yesterday,
            type: 'day'
        });

        // Week periods
        options.push({ value: 'separator1', label: '--- Weeks ---', disabled: true });

        const thisWeekStart = new Date(today);
        thisWeekStart.setDate(today.getDate() - today.getDay());
        options.push({
            value: 'this-week',
            label: 'This Week',
            startDate: thisWeekStart,
            endDate: today,
            type: 'week'
        });

        const lastWeekStart = new Date(thisWeekStart);
        lastWeekStart.setDate(thisWeekStart.getDate() - 7);
        const lastWeekEnd = new Date(thisWeekStart);
        lastWeekEnd.setDate(thisWeekStart.getDate() - 1);
        options.push({
            value: 'last-week',
            label: 'Last Week',
            startDate: lastWeekStart,
            endDate: lastWeekEnd,
            type: 'week'
        });

        // Month periods
        options.push({ value: 'separator2', label: '--- Months ---', disabled: true });

        const thisMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
        options.push({
            value: 'this-month',
            label: 'This Month',
            startDate: thisMonthStart,
            endDate: today,
            type: 'month'
        });

        const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
        options.push({
            value: 'last-month',
            label: 'Last Month',
            startDate: lastMonthStart,
            endDate: lastMonthEnd,
            type: 'month'
        });

        // Custom period
        options.push({ value: 'separator3', label: '--- Custom ---', disabled: true });
        options.push({
            value: 'custom',
            label: 'Custom Period',
            type: 'custom'
        });

        // CSV Days
        options.push({ value: 'separator4', label: '--- CSV Days ---', disabled: true });
        for (let day = 1; day <= 7; day++) {
            options.push({
                value: `day-${day}`,
                label: `Day ${day}`,
                csvDay: day,
                type: 'csv-day'
            });
        }

        return options;
    };

    const periodOptions = generatePeriodOptions();

    const handlePeriodSelect = (option) => {
        if (option.disabled) return;

        setSelectedOption(option.value);

        if (option.value === 'custom') {
            // Don't close dropdown for custom period
            return;
        } else if (option.type === 'csv-day') {
            onPeriodChange({
                csvDay: option.csvDay,
                type: option.type
            });
            setIsDropdownOpen(false);
        } else {
            onPeriodChange({
                start: option.startDate,
                end: option.endDate,
                type: option.type
            });
            setIsDropdownOpen(false);
        }
    };

    const handleCustomPeriodApply = () => {
        if (customStartDate && customEndDate) {
            const startDate = new Date(customStartDate);
            const endDate = new Date(customEndDate);

            if (startDate <= endDate) {
                onPeriodChange({
                    start: startDate,
                    end: endDate,
                    type: 'custom'
                });
                setSelectedOption('custom');
                setIsDropdownOpen(false);
            }
        }
    };

    const getMaxDate = () => {
        const today = new Date();
        return today.toISOString().split('T')[0];
    };

    const getMinDate = () => {
        const twoYearsAgo = new Date();
        twoYearsAgo.setFullYear(twoYearsAgo.getFullYear() - 2);
        return twoYearsAgo.toISOString().split('T')[0];
    };

    const getDisplayLabel = () => {
        if (!selectedPeriod) return 'Select Period';

        const option = periodOptions.find(opt => opt.value === selectedOption);
        if (option && selectedOption !== 'custom') {
            return option.label;
        }

        // Custom or period-based display
        const { start, end } = selectedPeriod;
        if (start && end) {
            const sameDay = start.toDateString() === end.toDateString();
            if (sameDay) {
                return start.toLocaleDateString('en-US', {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric'
                });
            } else {
                return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
            }
        }

        return 'Select Period';
    };

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="flex items-center space-x-1 bg-white dark:bg-gray-800 px-3 py-2 rounded text-sm border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
                <Calendar size={14} />
                <span>{getDisplayLabel()}</span>
                <ChevronDown
                    size={16}
                    className={`transform transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`}
                />
            </button>

            {/* Dropdown Menu */}
            {isDropdownOpen && (
                <div className="absolute right-0 mt-1 w-72 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-10 max-h-96 overflow-y-auto">
                    <div className="py-1">
                        {periodOptions.map((option) => (
                            <button
                                key={option.value}
                                onClick={() => handlePeriodSelect(option)}
                                disabled={option.disabled}
                                className={`w-full text-left px-3 py-2 text-sm transition-colors ${option.disabled
                                    ? 'text-gray-400 cursor-not-allowed font-medium'
                                    : selectedOption === option.value
                                        ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                                    }`}
                            >
                                {option.label}
                            </button>
                        ))}

                        {/* Custom Period Selector */}
                        {selectedOption === 'custom' && (
                            <div className="px-3 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
                                <label className="block text-xs text-gray-600 dark:text-gray-400 mb-2 font-medium">
                                    Select date range:
                                </label>
                                <div className="space-y-2">
                                    <div>
                                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">From:</label>
                                        <input
                                            type="date"
                                            min={getMinDate()}
                                            max={getMaxDate()}
                                            value={customStartDate}
                                            onChange={(e) => setCustomStartDate(e.target.value)}
                                            className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">To:</label>
                                        <input
                                            type="date"
                                            min={customStartDate || getMinDate()}
                                            max={getMaxDate()}
                                            value={customEndDate}
                                            onChange={(e) => setCustomEndDate(e.target.value)}
                                            className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                        />
                                    </div>
                                    <button
                                        onClick={handleCustomPeriodApply}
                                        disabled={!customStartDate || !customEndDate}
                                        className="w-full mt-2 px-3 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                                    >
                                        Apply Period
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

// Energy Usage Chart Component
const EnergyUsageChart = ({ view }) => {
    const [selectedDay, setSelectedDay] = useState(1);
    const [chartData, setChartData] = useState([]);
    const [hoveredBar, setHoveredBar] = useState(null);
    const [loading, setLoading] = useState(true);
    const [dataSource, setDataSource] = useState('');

    useEffect(() => {
        const loadChartData = async () => {
            try {
                setLoading(true);
                
                console.log('🔍 Loading chart data for Day:', selectedDay);
                
                // Choose CSV type based on view
                const csvType = view === 'community' ? 'FORECAST' : 'DATA';
                // Get CSV data
                const csvResult = await getParsedCsvData(csvType);
                const dayData = await getDataForDay(selectedDay, csvType, csvResult.data);
                
                console.log('📈 Day Data Sample:', dayData.slice(0, 3));
                
                // Create hourly data
                const hours = [];
                for (let hour = 0; hour < 24; hour++) {
                    const hourData = dayData.filter(row => {
                        const timeVal = row.time;
                        const rowHour = typeof timeVal === 'string' ? parseInt(timeVal.split(':')[0]) : Math.floor(parseFloat(timeVal));
                        return rowHour === hour;
                    });
                    
                    // Check for surplus - try different column names
                    const surplusValues = hourData.map(row => {
                        const surplus1 = parseFloat(row['Surplus (kW)'] || 0);
                        const surplus2 = parseFloat(row.surplus || 0);
                        const surplus3 = parseFloat(row.Surplus || 0);
                        return Math.max(surplus1, surplus2, surplus3);
                    });
                    
                    const hasSurplus = surplusValues.some(val => val > 0);
                    const maxSurplus = surplusValues.length > 0 ? Math.max(...surplusValues) : 0;
                    
                    console.log(`🕐 Hour ${hour}: hasSurplus=${hasSurplus}, maxSurplus=${maxSurplus}, values=[${surplusValues.join(', ')}]`);
                    
                    hours.push({
                        hour: hour,
                        hasSurplus: hasSurplus,
                        maxSurplus: maxSurplus.toFixed(4),
                        quarterHourData: hourData.map((row, index) => ({
                            time: typeof row.time === 'string' ? row.time : `${hour.toString().padStart(2, '0')}:${(index * 15).toString().padStart(2, '0')}`,
                            surplus: Math.max(
                                parseFloat(row['Surplus (kW)'] || 0),
                                parseFloat(row.surplus || 0),
                                parseFloat(row.Surplus || 0)
                            )
                        }))
                    });
                }
                
                setChartData(hours);
                setDataSource(`Real ${view === 'community' ? 'Community' : 'Household'} - Day ${selectedDay}`);
            } catch (error) {
                console.error('❌ Error loading chart data:', error);
                // Fallback data
                const fallbackData = [];
                for (let hour = 0; hour < 24; hour++) {
                    fallbackData.push({
                        hour: hour,
                        hasSurplus: Math.random() > 0.5,
                        maxSurplus: (Math.random() * 50).toFixed(1)
                    });
                }
                setChartData(fallbackData);
                setDataSource('Sample Data (CSV Error)');
            }
            
            setLoading(false);
        };

        loadChartData();
    }, [selectedDay, view]);

    if (loading) {
        return <div className="text-center py-8">Loading chart data...</div>;
    }

    return (
        <div className="space-y-4">
            {/* Day Selector and Data Source */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <label className="text-sm font-medium">Select Day:</label>
                    <select 
                        value={selectedDay} 
                        onChange={(e) => setSelectedDay(parseInt(e.target.value))}
                        className="px-3 py-1 border rounded-md text-sm"
                    >
                        {[1, 2, 3, 4, 5, 6, 7].map(day => (
                            <option key={day} value={day}>Day {day}</option>
                        ))}
                    </select>
                </div>
                <div className="flex items-center gap-2">
                    <div className={`w-3 h-3 rounded-full ${dataSource.includes('Real') ? 'bg-green-500' : 'bg-orange-500'}`}></div>
                    <span className="text-xs text-gray-600">{dataSource}</span>
                </div>
            </div>

            {/* Legend */}
            <div className="flex justify-center gap-8">
                <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-red-300 border border-red-500 rounded"></div>
                    <span className="text-sm">Do not use</span>
                </div>
                <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-yellow-200 border border-yellow-500 rounded"></div>
                    <span className="text-sm">Good time to use energy</span>
                </div>
            </div>

            {/* Chart */}
            <div className="relative">
                <div className="flex items-end justify-between h-32 border-b border-gray-300 px-2">
                    {chartData.map((data, index) => (
                        <div
                            key={index}
                            className="relative flex-1 mx-0.5 group cursor-pointer"
                            onMouseEnter={() => setHoveredBar(index)}
                            onMouseLeave={() => setHoveredBar(null)}
                        >
                            <div
                                className="w-full border-2 rounded-t-sm transition-all duration-200"
                                style={{
                                    height: '120px',
                                    backgroundColor: data.hasSurplus ? '#FEF3C7' : '#FCA5A5',
                                    borderColor: data.hasSurplus ? '#F59E0B' : '#EF4444',
                                    opacity: hoveredBar === index ? 0.8 : 1,
                                }}
                            >
                                {hoveredBar === index && (
                                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 z-10">
                                        <div className="bg-gray-800 text-white text-xs rounded px-3 py-2 whitespace-nowrap">
                                            <div className="font-medium mb-1">
                                                Day {selectedDay} - {data.hour.toString().padStart(2, '0')}:00 Hour
                                            </div>
                                            <div className="space-y-1">
                                                {data.quarterHourData.map((quarter, idx) => (
                                                    <div key={idx} className="flex justify-between gap-2">
                                                        <span>{quarter.time}</span>
                                                        <span className={quarter.surplus > 0 ? 'text-green-300' : 'text-red-300'}>
                                                            {quarter.surplus.toFixed(4)} kW
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                            <div className="border-t border-gray-600 mt-1 pt-1 text-center">
                                                <span className="text-yellow-300">
                                                    Max: {data.maxSurplus} kW
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {index % 2 === 0 && (
                                <div className="text-xs text-gray-600 text-center mt-1">
                                    {data.hour.toString().padStart(2, '0')}:00
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

// Fixed Dynamic Daily Profile Charts Component
const DynamicDailyProfileCharts = () => {
    const [householdData, setHouseholdData] = useState([]);
    const [householdLoading, setHouseholdLoading] = useState(true);
    const [householdError, setHouseholdError] = useState(null);
    const [householdChartData, setHouseholdChartData] = useState([]);

    const [communityData, setCommunityData] = useState([]);
    const [communityLoading, setCommunityLoading] = useState(true);
    const [communityError, setCommunityError] = useState(null);
    const [communityChartData, setCommunityChartData] = useState([]);

    // FIXED: Added separate state variables for each chart's period
    const [householdProfilePeriod, setHouseholdProfilePeriod] = useState({
        start: new Date(),
        end: new Date(),
        type: 'day'
    });
    const [communityProfilePeriod, setCommunityProfilePeriod] = useState({
        start: new Date(),
        end: new Date(),
        type: 'day'
    });

    const updateHouseholdChartData = useCallback(async () => {
        try {
            console.log('🔄 Updating household chart data for period:', householdProfilePeriod);

            const { start, type, csvDay } = householdProfilePeriod;
            
            if (type === 'csv-day' && csvDay) {
                console.log('📊 Household - Selected CSV day:', csvDay);
                
                // Fetch fresh data instead of using stale state
                const csvResult = await getParsedCsvData('DATA');
                const dayData = await getDataForDay(csvDay, 'DATA', csvResult.data);

                const transformedData = dayData.map(row => ({
                    time: row.time,
                    generation: row.generation_kW || row.generation || 0,
                    demand: row.demand_kW || row.demand || 0,
                    production: row.generation_kW || row.generation || 0,
                    consumption: row.demand_kW || row.demand || 0
                }));

                console.log('📊 Household - Data points:', transformedData.length);
                setHouseholdChartData(transformedData);
            } else if (type === 'day') {
                // Map calendar date to CSV day based on day of week
                const dayOfWeek = start.getDay(); // 0 = Sunday, 6 = Saturday
                const csvDay = (dayOfWeek % 7) + 1; // Map to CSV days 1-7
                
                console.log('📊 Household - Calendar date mapped to CSV day:', csvDay);
                
                const csvResult = await getParsedCsvData('DATA');
                const dayData = await getDataForDay(csvDay, 'DATA', csvResult.data);

                const transformedData = dayData.map(row => ({
                    time: row.time,
                    generation: row.generation_kW || row.generation || 0,
                    demand: row.demand_kW || row.demand || 0,
                    production: row.generation_kW || row.generation || 0,
                    consumption: row.demand_kW || row.demand || 0
                }));

                console.log('📊 Household - Data points:', transformedData.length);
                setHouseholdChartData(transformedData);
            } else {
                // Handle multi-day periods (weeks/months/custom) - generate sample data
                console.log('📊 Household - Generating sample data for period type:', type);
                const sampleData = generateSampleDataForPeriod(type, 'household');
                setHouseholdChartData(sampleData);
            }
        } catch (error) {
            console.error('Error updating household chart data:', error);
            // Fallback to sample data
            const sampleData = generateSampleDataForPeriod('day', 'household');
            setHouseholdChartData(sampleData);
        }
    }, [householdProfilePeriod]);

    const updateCommunityChartData = useCallback(async () => {
        try {
            console.log('🔄 Updating community chart data for period:', communityProfilePeriod);

            const { start, type, csvDay } = communityProfilePeriod;
            
            if (type === 'csv-day' && csvDay) {
                console.log('📊 Community - Selected CSV day:', csvDay);
                
                // Fetch fresh FORECAST data for the selected day
                const csvResult = await getParsedCsvData('FORECAST');
                const dayData = await getDataForDay(csvDay, 'FORECAST', csvResult.data);

                const transformedData = dayData.map(row => ({
                    time: row.time,
                    generation: row['Generation (kW)'] || row.generation || 0,
                    demand: row['Demand (kW)'] || row.demand || 0,
                    production: row['Generation (kW)'] || row.generation || 0,
                    consumption: row['Demand (kW)'] || row.demand || 0
                }));

                console.log('📊 Community - Data points:', transformedData.length, 'for Day', csvDay);
                setCommunityChartData(transformedData);
            } else if (type === 'day') {
                // Map calendar date to CSV day based on day of week
                const dayOfWeek = start.getDay(); // 0 = Sunday, 6 = Saturday
                const csvDay = (dayOfWeek % 7) + 1; // Map to CSV days 1-7
                
                console.log('📊 Community - Calendar date mapped to CSV day:', csvDay);
                
                const csvResult = await getParsedCsvData('FORECAST');
                const dayData = await getDataForDay(csvDay, 'FORECAST', csvResult.data);

                const transformedData = dayData.map(row => ({
                    time: row.time,
                    generation: row['Generation (kW)'] || row.generation || 0,
                    demand: row['Demand (kW)'] || row.demand || 0,
                    production: row['Generation (kW)'] || row.generation || 0,
                    consumption: row['Demand (kW)'] || row.demand || 0
                }));

                console.log('📊 Community - Data points:', transformedData.length, 'for Day', csvDay);
                setCommunityChartData(transformedData);
            } else {
                // Handle multi-day periods (weeks/months/custom) - generate sample data
                console.log('📊 Community - Generating sample data for period type:', type);
                const sampleData = generateSampleDataForPeriod(type, 'community');
                setCommunityChartData(sampleData);
            }
        } catch (error) {
            console.error('Error updating community chart data:', error);
            // Fallback to sample data
            const sampleData = generateSampleDataForPeriod('day', 'community');
            setCommunityChartData(sampleData);
        }
    }, [communityProfilePeriod]);

    // Helper function to generate sample data for different time periods
    const generateSampleDataForPeriod = (periodType, dataType = 'household') => {
        const data = [];
        
        if (periodType === 'week') {
            // Generate 7 days of data
            for (let day = 0; day < 7; day++) {
                for (let hour = 0; hour < 24; hour++) {
                    for (let mins = 0; mins < 60; mins += 15) {
                        const timeString = `Day ${day + 1} ${hour.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
                        
                        if (dataType === 'household') {
                            let generation = 0;
                            if (hour >= 6 && hour <= 18) {
                                const midday = 12;
                                const distance = Math.abs(hour + mins / 60 - midday);
                                generation = Math.max(0, 2 * Math.pow(Math.cos(distance * Math.PI / 12), 2) + Math.random() * 0.2);
                            }
                            let demand = 0.5;
                            if (hour >= 7 && hour <= 9) demand += 1.0;
                            if (hour >= 17 && hour <= 21) demand += 1.5;
                            demand += Math.random() * 0.3;
                            data.push({ 
                                time: timeString, 
                                generation: parseFloat(generation.toFixed(3)),
                                demand: parseFloat(demand.toFixed(3)),
                                production: parseFloat(generation.toFixed(3)),
                                consumption: parseFloat(demand.toFixed(3))
                            });
                        } else {
                            // Community data (generation + demand)
                            let generation = 0;
                            if (hour >= 6 && hour <= 18) {
                                const midday = 12;
                                const distance = Math.abs(hour + mins / 60 - midday);
                                generation = Math.max(0, 4 * Math.pow(Math.cos(distance * Math.PI / 12), 2) + Math.random() * 0.5);
                            }
                            let demand = 1.5;
                            if (hour >= 7 && hour <= 9) demand += 1.2;
                            if (hour >= 17 && hour <= 21) demand += 1.8;
                            demand += Math.random() * 0.4;
                            data.push({
                                time: timeString,
                                generation: parseFloat(generation.toFixed(3)),
                                demand: parseFloat(demand.toFixed(3)),
                                production: parseFloat(generation.toFixed(3)),
                                consumption: parseFloat(demand.toFixed(3))
                            });
                        }
                    }
                }
            }
        } else if (periodType === 'month') {
            // Generate 30 days of data (simplified - one point per day)
            for (let day = 0; day < 30; day++) {
                const timeString = `Day ${day + 1}`;
                
                if (dataType === 'household') {
                    let generation = Math.random() * 1.5;
                    let demand = 0.5 + Math.random() * 0.5;
                    data.push({ 
                        time: timeString, 
                        generation: parseFloat(generation.toFixed(3)),
                        demand: parseFloat(demand.toFixed(3)),
                        production: parseFloat(generation.toFixed(3)),
                        consumption: parseFloat(demand.toFixed(3))
                    });
                } else {
                    // Community data
                    let generation = Math.random() * 2;
                    let demand = 1.0 + Math.random() * 0.8;
                    data.push({
                        time: timeString,
                        generation: parseFloat(generation.toFixed(3)),
                        demand: parseFloat(demand.toFixed(3)),
                        production: parseFloat(generation.toFixed(3)),
                        consumption: parseFloat(demand.toFixed(3))
                    });
                }
            }
        } else if (periodType === 'custom') {
            // Generate data based on custom date range
            const daysDiff = Math.ceil((new Date() - new Date()) / (1000 * 60 * 60 * 24)) + 1;
            const daysToGenerate = Math.min(daysDiff, 30); // Limit to 30 days max
            
            for (let day = 0; day < daysToGenerate; day++) {
                const timeString = `Day ${day + 1}`;
                
                if (dataType === 'household') {
                    let generation = Math.random() * 1.5;
                    let demand = 0.5 + Math.random() * 0.5;
                    data.push({ 
                        time: timeString, 
                        generation: parseFloat(generation.toFixed(3)),
                        demand: parseFloat(demand.toFixed(3)),
                        production: parseFloat(generation.toFixed(3)),
                        consumption: parseFloat(demand.toFixed(3))
                    });
                } else {
                    // Community data
                    let generation = Math.random() * 2;
                    let demand = 1.0 + Math.random() * 0.8;
                    data.push({
                        time: timeString,
                        generation: parseFloat(generation.toFixed(3)),
                        demand: parseFloat(demand.toFixed(3)),
                        production: parseFloat(generation.toFixed(3)),
                        consumption: parseFloat(demand.toFixed(3))
                    });
                }
            }
        } else {
            // Default: single day data
            for (let hour = 0; hour < 24; hour++) {
                for (let mins = 0; mins < 60; mins += 15) {
                    const timeString = `${hour.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
                    
                    if (dataType === 'household') {
                        let generation = 0;
                        if (hour >= 6 && hour <= 18) {
                            const midday = 12;
                            const distance = Math.abs(hour + mins / 60 - midday);
                            generation = Math.max(0, 2 * Math.pow(Math.cos(distance * Math.PI / 12), 2) + Math.random() * 0.2);
                        }
                        let demand = 0.5;
                        if (hour >= 7 && hour <= 9) demand += 1.0;
                        if (hour >= 17 && hour <= 21) demand += 1.5;
                        demand += Math.random() * 0.3;
                        data.push({ 
                            time: timeString, 
                            generation: parseFloat(generation.toFixed(3)),
                            demand: parseFloat(demand.toFixed(3)),
                            production: parseFloat(generation.toFixed(3)),
                            consumption: parseFloat(demand.toFixed(3))
                        });
                    } else {
                        // Community data
                        let generation = 0;
                        if (hour >= 6 && hour <= 18) {
                            const midday = 12;
                            const distance = Math.abs(hour + mins / 60 - midday);
                            generation = Math.max(0, 4 * Math.pow(Math.cos(distance * Math.PI / 12), 2) + Math.random() * 0.5);
                        }
                        let demand = 1.5;
                        if (hour >= 7 && hour <= 9) demand += 1.2;
                        if (hour >= 17 && hour <= 21) demand += 1.8;
                        demand += Math.random() * 0.4;
                        data.push({
                            time: timeString,
                            generation: parseFloat(generation.toFixed(3)),
                            demand: parseFloat(demand.toFixed(3)),
                            production: parseFloat(generation.toFixed(3)),
                            consumption: parseFloat(demand.toFixed(3))
                        });
                    }
                }
            }
        }
        
        return data;
    };

    // Load initial data
    useEffect(() => {
        loadHouseholdData();
        loadCommunityData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Update chart data when period changes
    useEffect(() => {
        if (householdData.length > 0) {
            updateHouseholdChartData();
        }
    }, [householdProfilePeriod, householdData, updateHouseholdChartData]);

    useEffect(() => {
        if (communityData.length > 0) {
            updateCommunityChartData();
        }
    }, [communityProfilePeriod, communityData, updateCommunityChartData]);

    const loadHouseholdData = async () => {
        try {
            setHouseholdLoading(true);
            setHouseholdError(null);

            console.log("🔄 Loading household data from DATA CSV...");
            const csvResult = await getParsedCsvData('DATA');

            if (csvResult.totalRows === 0) {
                throw new Error("No valid household data found");
            }

            const availableDays = await getAvailableDays('DATA', csvResult.data);
            const selectedDay = availableDays[0] || 1;
            const dayData = await getDataForDay(selectedDay, 'DATA', csvResult.data);

            const transformedData = dayData.map(row => ({
                time: row.time,
                generation: row.generation || 0,
                demand: row.demand || 0,
                production: row.production || row.generation || 0,
                consumption: row.consumption || row.demand || 0
            }));

            setHouseholdData(csvResult.data);
            setHouseholdChartData(transformedData);
            setHouseholdLoading(false);
            console.log("✅ Household data loaded successfully!");

        } catch (err) {
            console.error('❌ Error loading household data:', err);
            setHouseholdError(`Failed to load household data: ${err.message}`);
            setHouseholdLoading(false);
            generateHouseholdFallback();
        }
    };

    const loadCommunityData = async () => {
        try {
            setCommunityLoading(true);
            setCommunityError(null);

            console.log("🔄 Loading community data from FORECAST CSV...");
            const csvResult = await getParsedCsvData('FORECAST');

            if (csvResult.totalRows === 0) {
                throw new Error("No valid community data found");
            }

            const availableDays = await getAvailableDays('FORECAST', csvResult.data);
            const selectedDay = availableDays[0] || 1;
            const dayData = await getDataForDay(selectedDay, 'FORECAST', csvResult.data);

            const transformedData = dayData.map(row => ({
                time: row.time,
                generation: row.generation || 0,
                demand: row.demand || 0,
                production: row.production || row.generation || 0,
                consumption: row.consumption || row.demand || 0
            }));

            setCommunityData(csvResult.data);
            setCommunityChartData(transformedData);
            setCommunityLoading(false);
            console.log("✅ Community data loaded successfully!");

        } catch (err) {
            console.error('❌ Error loading community data:', err);
            setCommunityError(`Failed to load community data: ${err.message}`);
            setCommunityLoading(false);
            generateCommunityFallback();
        }
    };

    const generateHouseholdFallback = () => {
        console.log("🔄 Generating household fallback data...");
        const fallbackData = [];
        for (let hour = 0; hour < 24; hour++) {
            for (let mins = 0; mins < 60; mins += 15) {
                const timeString = `${hour.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;

                let generation = 0;
                if (hour >= 6 && hour <= 18) {
                    const midday = 12;
                    const distance = Math.abs(hour + mins / 60 - midday);
                    generation = Math.max(0, 2 * Math.pow(Math.cos(distance * Math.PI / 12), 2) + Math.random() * 0.2);
                }

                let demand = 0.5;
                if (hour >= 7 && hour <= 9) demand += 1.0;
                if (hour >= 17 && hour <= 21) demand += 1.5;
                demand += Math.random() * 0.3;

                fallbackData.push({
                    time: timeString,
                    generation: parseFloat(generation.toFixed(3)),
                    demand: parseFloat(demand.toFixed(3)),
                    production: parseFloat(generation.toFixed(3)),
                    consumption: parseFloat(demand.toFixed(3))
                });
            }
        }
        setHouseholdChartData(fallbackData);
    };

    const generateCommunityFallback = () => {
        console.log("🔄 Generating community fallback data...");
        const fallbackData = [];
        for (let hour = 0; hour < 24; hour++) {
            for (let mins = 0; mins < 60; mins += 15) {
                const timeString = `${hour.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;

                let generation = 0;
                if (hour >= 6 && hour <= 18) {
                    const midday = 12;
                    const distance = Math.abs(hour + mins / 60 - midday);
                    generation = Math.max(0, 4 * Math.pow(Math.cos(distance * Math.PI / 12), 2) + Math.random() * 0.5);
                }
                generation += Math.random() * 0.8;

                let demand = 1.5;
                if (hour >= 7 && hour <= 9) demand += 1.2;
                if (hour >= 17 && hour <= 21) demand += 1.8;
                demand += Math.random() * 0.4;

                fallbackData.push({
                    time: timeString,
                    generation: parseFloat(generation.toFixed(3)),
                    demand: parseFloat(demand.toFixed(3)),
                    production: parseFloat(generation.toFixed(3)),
                    consumption: parseFloat(demand.toFixed(3))
                });
            }
        }
        setCommunityChartData(fallbackData);
    };

    const LineChartTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <div className="bg-white dark:bg-gray-800 p-3 border border-gray-300 dark:border-gray-600 rounded shadow-lg">
                    <p className="font-medium text-gray-900 dark:text-gray-100">{`Time: ${label}`}</p>
                    {payload.map((entry, index) => (
                        <p key={index} style={{ color: entry.color }} className="text-sm">
                            {`${entry.name}: ${entry.value.toFixed(3)} kW`}
                        </p>
                    ))}
                </div>
            );
        }
        return null;
    };

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Daily Profile (Household) - Fixed Layout */}
            <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
                <div className="flex justify-between items-center mb-2">
                    <h3 className="font-medium mb-4 text-gray-900 dark:text-gray-100">Daily Profile (Household)</h3>
                    <TimelineDropdown
                        selectedPeriod={householdProfilePeriod}
                        onPeriodChange={setHouseholdProfilePeriod}
                    />
                </div>

                {householdLoading ? (
                    <div className="h-80 flex items-center justify-center">
                        <div className="text-center">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
                            <p className="text-gray-600 dark:text-gray-400 text-sm">Loading household data...</p>
                        </div>
                    </div>
                ) : householdError ? (
                    <div className="h-80 flex items-center justify-center">
                        <div className="text-center">
                            <p className="text-red-600 dark:text-red-400 text-sm mb-1">{householdError}</p>
                            <p className="text-gray-600 dark:text-gray-400 text-xs">Using sample data</p>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {/* Chart Container - Fixed Height */}
                        <div className="h-64">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart
                                    data={householdChartData}
                                    margin={{ top: 10, right: 20, left: 10, bottom: 20 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" opacity={0.5} />
                                    <XAxis
                                        dataKey="time"
                                        tick={{ fontSize: 10 }}
                                        tickLine={{ stroke: '#9ca3af' }}
                                        axisLine={{ stroke: '#9ca3af' }}
                                        interval="preserveStartEnd"
                                        tickFormatter={(value, index) => {
                                            // For multi-day data (less than 40 points), show more ticks
                                            if (householdChartData.length <= 40) {
                                                // Show every 2-3 ticks for weeks/months
                                                const interval = householdChartData.length <= 10 ? 1 : Math.ceil(householdChartData.length / 8);
                                                if (index % interval === 0) return value;
                                            } else {
                                                // For single day with many points, show fewer ticks
                                                if (index % 20 === 0) return value;
                                            }
                                            return '';
                                        }}
                                    />
                                    <YAxis
                                        tick={{ fontSize: 10 }}
                                        tickLine={{ stroke: '#9ca3af' }}
                                        axisLine={{ stroke: '#9ca3af' }}
                                        label={{
                                            value: 'Power (kW)',
                                            angle: -90,
                                            position: 'insideLeft',
                                            style: { textAnchor: 'middle', fontSize: '11px' }
                                        }}
                                    />
                                    <Tooltip content={<LineChartTooltip />} />
                                    <Line
                                        type="monotone"
                                        dataKey="generation"
                                        stroke="#00C49F"
                                        strokeWidth={2}
                                        name="Generation"
                                        dot={false}
                                        activeDot={{ r: 4 }}
                                    />
                                    <Line
                                        type="monotone"
                                        dataKey="demand"
                                        stroke="#FF8042"
                                        strokeWidth={2}
                                        name="Demand"
                                        dot={false}
                                        activeDot={{ r: 4 }}
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>

                        {/* Legend - Fixed Position */}
                        <div className="flex justify-center space-x-6 py-2 border-t border-gray-200 dark:border-gray-600">
                            <div className="flex items-center">
                                <div className="w-4 h-4 bg-green-500 rounded-full mr-2"></div>
                                <span className="text-sm text-gray-700 dark:text-gray-300">Generation</span>
                            </div>
                            <div className="flex items-center">
                                <div className="w-4 h-4 bg-orange-500 rounded-full mr-2"></div>
                                <span className="text-sm text-gray-700 dark:text-gray-300">Demand</span>
                            </div>
                        </div>

                        {/* Data Info - Fixed Position */}
                        <div className="text-center text-xs text-gray-500 dark:text-gray-400 py-2 bg-gray-50 dark:bg-gray-700 rounded">
                            <div className="font-medium">
                                Household Energy Profile
                            </div>
                            <div className="mt-1">
                                {householdChartData.length} data points • 15-minute intervals
                            </div>
                            <div className="text-xs opacity-75 mt-1">
                                Data Source: DATA CSV
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Daily Profile (Community) - Fixed Layout */}
            <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
                <div className="flex justify-between items-center mb-2">
                    <h3 className="font-medium mb-4 text-gray-900 dark:text-gray-100">Daily Profile (Community)</h3>
                    <TimelineDropdown
                        selectedPeriod={communityProfilePeriod}
                        onPeriodChange={setCommunityProfilePeriod}
                    />
                </div>

                {communityLoading ? (
                    <div className="h-80 flex items-center justify-center">
                        <div className="text-center">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500 mx-auto mb-2"></div>
                            <p className="text-gray-600 dark:text-gray-400 text-sm">Loading community data...</p>
                        </div>
                    </div>
                ) : communityError ? (
                    <div className="h-80 flex items-center justify-center">
                        <div className="text-center">
                            <p className="text-red-600 dark:text-red-400 text-sm mb-1">{communityError}</p>
                            <p className="text-gray-600 dark:text-gray-400 text-xs">Using sample data</p>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {/* Chart Container - Fixed Height */}
                        <div className="h-64">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart
                                    data={communityChartData}
                                    margin={{ top: 10, right: 20, left: 10, bottom: 20 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" opacity={0.5} />
                                    <XAxis
                                        dataKey="time"
                                        tick={{ fontSize: 10 }}
                                        tickLine={{ stroke: '#9ca3af' }}
                                        axisLine={{ stroke: '#9ca3af' }}
                                        interval="preserveStartEnd"
                                        tickFormatter={(value, index) => {
                                            // For multi-day data (less than 40 points), show more ticks
                                            if (communityChartData.length <= 40) {
                                                // Show every 2-3 ticks for weeks/months
                                                const interval = communityChartData.length <= 10 ? 1 : Math.ceil(communityChartData.length / 8);
                                                if (index % interval === 0) return value;
                                            } else {
                                                // For single day with many points, show fewer ticks
                                                if (index % 20 === 0) return value;
                                            }
                                            return '';
                                        }}
                                    />
                                    <YAxis
                                        tick={{ fontSize: 10 }}
                                        tickLine={{ stroke: '#9ca3af' }}
                                        axisLine={{ stroke: '#9ca3af' }}
                                        label={{
                                            value: 'Power (kW)',
                                            angle: -90,
                                            position: 'insideLeft',
                                            style: { textAnchor: 'middle', fontSize: '11px' }
                                        }}
                                    />
                                    <Tooltip content={<LineChartTooltip />} />
                                    <Line
                                        type="monotone"
                                        dataKey="generation"
                                        stroke="#00C49F"
                                        strokeWidth={2}
                                        name="Generation"
                                        dot={false}
                                        activeDot={{ r: 4 }}
                                    />
                                    <Line
                                        type="monotone"
                                        dataKey="demand"
                                        stroke="#FF8042"
                                        strokeWidth={2}
                                        name="Demand"
                                        dot={false}
                                        activeDot={{ r: 4 }}
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>

                        {/* Legend - Fixed Position */}
                        <div className="flex justify-center space-x-6 py-2 border-t border-gray-200 dark:border-gray-600">
                            <div className="flex items-center">
                                <div className="w-4 h-4 bg-green-500 rounded-full mr-2"></div>
                                <span className="text-sm text-gray-700 dark:text-gray-300">Generation</span>
                            </div>
                            <div className="flex items-center">
                                <div className="w-4 h-4 bg-orange-500 rounded-full mr-2"></div>
                                <span className="text-sm text-gray-700 dark:text-gray-300">Demand</span>
                            </div>
                        </div>

                        {/* Data Info - Fixed Position */}
                        <div className="text-center text-xs text-gray-500 dark:text-gray-400 py-2 bg-gray-50 dark:bg-gray-700 rounded">
                            <div className="font-medium">
                                Community Energy Profile
                            </div>
                            <div className="mt-1">
                                {communityChartData.length} data points • 15-minute intervals
                            </div>
                            <div className="text-xs opacity-75 mt-1">
                                Data Source: FORECAST CSV
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

const PvActionsTab = ({ view, setView }) => {
    // Current surplus values for horse animation
    const [currentHouseholdSurplus, setCurrentHouseholdSurplus] = useState(0);
    const [currentCommunitySurplus, setCurrentCommunitySurplus] = useState(0);

    // Helper function to parse time string to decimal hours
    const parseTimeToDecimal = (timeStr) => {
        const [hours, minutes, seconds] = timeStr.split(':').map(Number);
        return hours + minutes / 60 + seconds / 3600;
    };

    // Function to calculate current surplus using real CSV data
    const calculateCurrentSurplus = useCallback(async () => {
        try {
            // For now, use current time (we'll update this to use selected day later)
            const now = new Date();
            const currentHour = now.getHours();
            const currentMinute = now.getMinutes();
            
            // Calculate which 15-minute block we're in
            const quarterHour = Math.floor(currentMinute / 15);
            const targetTimeDecimal = currentHour + quarterHour * 0.25;
            
            console.log('🕐 Current time:', currentHour, ':', currentMinute, '→', targetTimeDecimal);
            
            // Get real data from CSV
            const householdCsvData = await getParsedCsvData('DATA');
            const communityCsvData = await getParsedCsvData('FORECAST');
            
            // Find the closest time match in household data (DATA CSV)
            let householdSurplus = 0;
            if (householdCsvData.data && householdCsvData.data.length > 0) {
                const householdMatch = householdCsvData.data.find(row => {
                    const rowTime = parseFloat(row.Time);
                    return Math.abs(rowTime - targetTimeDecimal) < 0.2; // Within 12 minutes
                });
                
                if (householdMatch) {
                    householdSurplus = parseFloat(householdMatch['Surplus (kW)']) || 0;
                    console.log('🏠 Household surplus found:', householdSurplus, 'kW at time', householdMatch.Time);
                } else {
                    console.log('🏠 No household data match found for time', targetTimeDecimal);
                }
            }
            
            // Find the closest time match in community data (FORECAST CSV)
            let communitySurplus = 0;
            if (communityCsvData.data && communityCsvData.data.length > 0) {
                const communityMatch = communityCsvData.data.find(row => {
                    const timeStr = row.Time || '00:00:00';
                    const timeDecimal = parseTimeToDecimal(timeStr);
                    return Math.abs(timeDecimal - targetTimeDecimal) < 0.2; // Within 12 minutes
                });
                
                if (communityMatch) {
                    communitySurplus = parseFloat(communityMatch['Surplus (kW)']) || 0;
                    console.log('🏘️ Community surplus found:', communitySurplus, 'kW at time', communityMatch.Time);
                } else {
                    console.log('🏘️ No community data match found for time', targetTimeDecimal);
                }
            }
            
            setCurrentHouseholdSurplus(householdSurplus);
            setCurrentCommunitySurplus(communitySurplus);
            
        } catch (error) {
            console.error('Error calculating current surplus:', error);
            // Fallback to zero if there's an error
            setCurrentHouseholdSurplus(0);
            setCurrentCommunitySurplus(0);
        }
    }, []);

    // Update current surplus every minute (simulate real-time)
    useEffect(() => {
        calculateCurrentSurplus();
        const interval = setInterval(() => {
            calculateCurrentSurplus();
        }, 60000); // Update every minute

        return () => clearInterval(interval);
    }, [calculateCurrentSurplus]);
    return (
        <div className='w-full'>
            {/* Container for the view buttons - positioned at top right */}
            <div className="flex justify-end mb-4">
                <div className="flex items-center space-x-2">
                    <span className="text-xs text-gray-500 dark:text-gray-400">View:</span>
                    <div className="flex items-center space-x-1 bg-gray-100 dark:bg-gray-700 rounded-full p-1">
                        <button
                            className={`text-xs px-2 py-1 rounded-full ${view === 'household' ? 'bg-blue-500 text-white' : 'text-gray-500 dark:text-gray-400'}`}
                            onClick={() => setView('household')}
                        >
                            Household
                        </button>
                        <button
                            className={`text-xs px-2 py-1 rounded-full ${view === 'community' ? 'bg-blue-500 text-white' : 'text-gray-500 dark:text-gray-400'}`}
                            onClick={() => setView('community')}
                        >
                            Community
                        </button>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 gap-6">
                {/* Best time to use Energy over the next 24 hours */}
                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
                    <h3 className="font-medium mb-4">Best time to use Energy over the next 24 hours</h3>
                    <EnergyUsageChart view={view} />

                    {/* Y/N Indicators */}
                    <div className="mt-6">
                        {/* Household Surplus Indicator */}
                        {view === 'household' && (
                            <div className="mb-4">
                                <div className="grid grid-cols-3 gap-4">
                                    <div className="col-span-2 bg-blue-100 p-2 flex items-center rounded">
                                        <span className="text-sm">Surplus Now- Household ({currentHouseholdSurplus.toFixed(2)} kW)</span>
                                    </div>
                                    <div className="flex items-center justify-center">
                                        <HorseAnimation 
                                            surplus={currentHouseholdSurplus} 
                                            size="medium" 
                                            label="Household"
                                            thresholds={{ stationary: 0.3, walking: 1.0 }}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Community Surplus Indicator */}
                        {view === 'community' && (
                            <div className="mb-4">
                                <div className="grid grid-cols-3 gap-4">
                                    <div className="col-span-2 bg-blue-100 p-2 flex items-center rounded">
                                        <span className="text-sm">Surplus Now- Community ({currentCommunitySurplus.toFixed(2)} kW)</span>
                                    </div>
                                    <div className="flex items-center justify-center">
                                        <HorseAnimation 
                                            surplus={currentCommunitySurplus} 
                                            size="medium" 
                                            label="Community"
                                            thresholds={{ stationary: 0, walking: 150 }}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* EV Charging */}
                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
                    <h3 className="font-medium mb-4">EV Charging</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* EV Station 1 */}
                        <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                            <div className="grid grid-cols-3 gap-4">
                                <div className="border border-gray-300 dark:border-gray-600 rounded p-2 flex items-center justify-center">
                                    <span className="text-sm font-medium">EV Charging Station 1</span>
                                </div>

                                <div className="flex flex-col items-center">
                                    <div className="text-xs mb-1">Availability Now</div>
                                    <div className="w-10 h-10 rounded-full bg-red-200 flex items-center justify-center">
                                        <span className="font-bold">N</span>
                                    </div>
                                </div>

                                <div className="flex flex-col items-center">
                                    <div className="text-xs mb-1">When next Available</div>
                                    <div className="border border-gray-300 dark:border-gray-600 rounded p-2 text-center w-full">
                                        11:30am
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* EV Station 2 */}
                        <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                            <div className="grid grid-cols-3 gap-4">
                                <div className="border border-gray-300 dark:border-gray-600 rounded p-2 flex items-center justify-center">
                                    <span className="text-sm font-medium">EV Charging Station 2</span>
                                </div>

                                <div className="flex flex-col items-center">
                                    <div className="text-xs mb-1">Availability Now</div>
                                    <div className="w-10 h-10 rounded-full bg-green-200 flex items-center justify-center">
                                        <span className="font-bold">Y</span>
                                    </div>
                                </div>

                                <div className="flex flex-col items-center">
                                    <div className="text-xs mb-1">When next Available</div>
                                    <div className="border border-gray-300 dark:border-gray-600 rounded p-2 text-center w-full">
                                        {/* Empty - already available */}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Daily Profile Charts - Now Dynamic */}
                <DynamicDailyProfileCharts />
            </div>
        </div>
    );
};

export default PvActionsTab;